import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Tag } from "./Reveal";
import { CTA } from "./CTA";
import { scrollToTarget } from "../lib/scroll";

const TERMS = [
  { k: "Price", v: "$5,000 flat" },
  { k: "Cabinets", v: "Up to 18, installed" },
  { k: "Countertops", v: "Granite, included" },
  { k: "Timeline", v: "Installed in days" },
];

export const Guarantee = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} className="relative min-h-[90vh] lg:min-h-screen overflow-hidden flex items-end" data-testid="guarantee-section">
      <motion.img
        src="/images/kitchen-island.png"
        alt="A modern kitchen with charcoal cabinets, a marble waterfall island and brass pendants"
        className="absolute inset-0 h-[116%] w-full object-cover -top-[8%]"
        style={{ y }}
        data-testid="guarantee-image"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/35 to-ink/10" />
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-ink/70 to-transparent" />

      <div className="relative w-full mx-auto max-w-[1600px] px-5 sm:px-8 pt-40 pb-12 sm:pb-20 grid lg:grid-cols-12 gap-10 items-end">
        <Reveal className="lg:col-span-7">
          <Tag light>The price is the price</Tag>
          <p className="mt-6 font-display font-semibold text-bone tracking-[-0.03em] leading-[0.94] text-[clamp(3.2rem,7.6vw,8.5rem)]" data-testid="guarantee-headline">
            $5K.<br />Guaranteed.
          </p>
          <p className="mt-6 max-w-lg text-lg sm:text-xl text-bone/85 leading-snug font-medium">
            New cabinets (up to 18) and granite countertops for a guaranteed $5,000. Not "starting at." Not "around." That's the number.
          </p>
        </Reveal>

        <Reveal className="lg:col-span-5 lg:col-start-8" delay={0.15}>
          <div className="bg-ink text-bone" data-testid="guarantee-terms">
            <dl className="divide-y divide-bone/15">
              {TERMS.map((t) => (
                <div key={t.k} className="flex items-baseline justify-between gap-6 px-6 sm:px-8 py-5">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-bone/50">{t.k}</dt>
                  <dd className="font-display font-medium text-xl sm:text-2xl tracking-[-0.015em]">{t.v}</dd>
                </div>
              ))}
            </dl>
            <div className="p-3 border-t border-bone/15">
              <CTA variant="teal" onClick={() => scrollToTarget("#quote-form")} testId="guarantee-cta-btn" className="w-full">
                Lock in my $5K kitchen
              </CTA>
            </div>
          </div>
          <p className="mt-4 text-xs text-bone/80 leading-relaxed">More than 18 cabinets, or adding a backsplash, tile or flooring? We'll quote that clearly up front.</p>
        </Reveal>
      </div>
    </section>
  );
};
